import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { Attribute, AttributesResource, AttributeTypeEnumType } from '@mark43/rms-api';

export const ATTRIBUTES_BY_TYPE_CACHE_KEY = 'attributes-by-type';

interface UseAttributesByTypeReturnType extends Omit<UseQueryResult<Attribute[]>, 'data'> {
    attributes: Attribute[];
}

export function useAttributesByType({
    attributeType,
    parentAttributeId,
    params,
}: {
    attributeType: AttributeTypeEnumType;
    parentAttributeId?: number;
    params?: AttributesResource.GetAttributesForTypes['params'];
}): UseAttributesByTypeReturnType {
    const apiClient = useApiClient();

    const { data: attributes, ...rest } = useQuery(
        [ATTRIBUTES_BY_TYPE_CACHE_KEY, attributeType, parentAttributeId, params],
        () => {
            return apiClient
                .request<AttributesResource.GetAttributesForTypes>({
                    url: 'attributes/types/',
                    method: 'POST',
                    data: [attributeType],
                    service: 'RMS',
                    params,
                })
                .then((resp): Attribute[] => {
                    if (parentAttributeId) {
                        return (
                            resp.data.data?.filter((attr) => {
                                return attr.parentAttributeId === parentAttributeId;
                            }) ?? []
                        );
                    }
                    return resp.data.data ?? [];
                });
        },
        { refetchOnMount: false, cacheTime: Infinity, staleTime: Infinity }
    );

    return { attributes: attributes ?? [], ...rest };
}
